import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { StoreProduct } from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useMedia } from '@/features/media';
import {
  ScopeChip,
  SearchEnginesCard,
  SearchListingSummary,
  type SharedProductSections,
} from '@/features/products';
import {
  resolveSeoPreview,
  SeoOverrideFields,
  SeoPreviewCard,
  useSeoStoreContext,
} from '@/features/seo';

import { useSearchEnginesModal } from '../hooks/use-search-engines-modal';
import type {
  StoreProductFormInput,
  StoreProductFormOutput,
} from '../schemas/store-product-form-schema';

export interface StoreSearchEnginesCardProps {
  storeId: string;
  row: StoreProduct;
  /** The master's cards; absent if the master could not be read. */
  shared?: SharedProductSections | undefined;
}

/**
 * This store's listing on search and answer engines, and the Summary and FAQ every store shares.
 * One modal holds both: the listing writes this store, the answers write the master product, and
 * one Save takes each to its own place. Blank SEO fields show the master's own, as the storefront
 * resolves them.
 */
export function StoreSearchEnginesCard({ storeId, row, shared }: StoreSearchEnginesCardProps) {
  const { t } = useTranslation(['storeCatalog', 'products']);
  const { can } = usePermissions();
  const form = useFormContext<StoreProductFormInput, unknown, StoreProductFormOutput>();
  const edit = useSearchEnginesModal(form, shared?.form);
  const seoStore = useSeoStoreContext(storeId);
  const master = row.product;
  const [title, handle, descriptionHtml, status, seo, featuredMediaId] = form.watch([
    'title',
    'handle',
    'descriptionHtml',
    'status',
    'seo',
    'featuredMediaId',
  ]);
  const featuredId = featuredMediaId ?? master.featuredMediaId;
  const featured = useMedia(featuredId);
  const path = `/products/${handle}`;
  const description = descriptionHtml || (master.descriptionHtml ?? master.summary);
  const live = seoStore
    ? resolveSeoPreview(
        {
          title: title || master.title,
          description,
          path,
          published: status === 'published',
          override: {
            title: seo.title || (master.seo?.title ?? ''),
            description: seo.description || (master.seo?.description ?? ''),
            canonical: seo.canonical || (master.seo?.canonical ?? ''),
            noIndex: seo.noIndex || Boolean(master.seo?.noIndex),
          },
          image: featuredId ? { alt: featured.data?.alt ?? null } : 'none',
        },
        seoStore,
      )
    : null;
  const sharedLine = (
    <p className="text-muted-foreground text-[13px]">{t('products:searchCard.sharedAnswers')}</p>
  );
  const ownSeo = Boolean(seo.title || seo.description || seo.canonical || seo.noIndex);
  const note = [
    handle !== master.handle ? t('storeCatalog:storePage.search.ownHandle') : null,
    ownSeo
      ? t('storeCatalog:storePage.search.ownSeo')
      : t('storeCatalog:storePage.search.inheritsSeo'),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <SearchEnginesCard
      badge={<ScopeChip scope="store" />}
      action={
        can('products.write') ? (
          <Button
            type="button"
            variant="link"
            size="xs"
            className="text-primary h-auto p-0 text-[13px]"
            onClick={edit.show}
          >
            {t('products:scope.edit')}
          </Button>
        ) : null
      }
      modal={edit.modal}
      errorTab={edit.errorTab}
      answersBadge={
        shared?.answersBadge ? <Badge variant="secondary">{shared.answersBadge}</Badge> : null
      }
      listing={
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="flex min-w-0 flex-col gap-4">
            <FormField
              control={form.control}
              name="handle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('storeCatalog:fields.handle')}</FormLabel>
                  <FormControl>
                    <Input {...field} className="font-mono text-[13px]" />
                  </FormControl>
                  <FormDescription>
                    {t('storeCatalog:storePage.search.handleHint', { master: master.handle })}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <SeoOverrideFields bare />
          </div>
          <SeoPreviewCard live={live} saved={null} path={path} bare />
        </div>
      }
      answers={shared?.answers ?? sharedLine}
    >
      <SearchListingSummary
        path={path}
        title={live?.title ?? (title || master.title)}
        description={live?.description ?? description ?? null}
        warnings={live?.warnings ?? []}
        note={note}
      />
      {sharedLine}
    </SearchEnginesCard>
  );
}
